/* biome-ignore-all lint/suspicious/noExplicitAny: reading content table */
/* biome-ignore-all lint/suspicious/noArrayIndexKey: vocab and parts list */
import type React from "react"
import { useState } from "react"
import { Table, Tag, Typography, Tooltip, Button, type TableProps, type TablePaginationConfig } from "antd"
import { EyeOutlined, LinkOutlined, FolderOutlined, DownOutlined, RightOutlined } from "@ant-design/icons"
import { useTableScroll } from "@/hooks/useTableScroll"
import type { ReadingSetItem, ReadingContentItem } from "../types"
import { DIFFICULTY_MAP, STATUS_MAP } from "./ReadingContentDetailModal"
import dayjs from "dayjs"
import "@/styles/table.full.css"

const { Text } = Typography

interface ReadingContentTableProps {
  data: ReadingSetItem[]
  loading: boolean
  onViewDetail: (readingItem: ReadingContentItem, index: number, list: ReadingContentItem[]) => void
  pagination?: TableProps<ReadingSetItem>["pagination"]
}

export const ReadingContentTable: React.FC<ReadingContentTableProps> = ({
  data,
  loading,
  onViewDetail,
  pagination,
}) => {
  const { scrollConfig, tableWrapperRef } = useTableScroll()
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([])

  const handleToggleExpandAll = () => {
    if (expandedRowKeys.length === data.length) {
      setExpandedRowKeys([])
    } else {
      setExpandedRowKeys(data.map((item) => item.id))
    }
  }

  // Sub-table rendering reading_contents of a set
  const expandedRowRender = (setRecord: ReadingSetItem) => {
    const readings = setRecord.reading_contents || []

    const subColumns: TableProps<ReadingContentItem>["columns"] = [
      {
        title: "Part",
        dataIndex: "part_number",
        key: "part_number",
        width: 80,
        align: "center",
        render: (part: number) => (
          <Tag color="cyan" className="m-0 font-bold font-mono text-xs">
            Part {part}
          </Tag>
        ),
      },
      {
        title: "Tiêu đề bài đọc",
        dataIndex: "title",
        key: "title",
        render: (title: string, record) => {
          return (
            <div className="flex flex-col gap-0.5">
              <span className="font-semibold text-gray-800 text-xs">
                {title || `Bài đọc Part ${record.part_number}`}
              </span>
              {record.genre && <span className="text-[11px] text-gray-400">Thể loại: {record.genre}</span>}
            </div>
          )
        },
      },
      {
        title: "Độ khó",
        dataIndex: "difficulty",
        key: "difficulty",
        width: 120,
        align: "center",
        render: (diff: string) => {
          const info = DIFFICULTY_MAP[diff]
          if (info) {
            return (
              <Tag color={info.color} className="m-0 font-medium text-xs">
                {info.label}
              </Tag>
            )
          }
          return <Tag className="m-0 text-xs">{diff || "—"}</Tag>
        },
      },
      {
        title: "Từ vựng mục tiêu",
        key: "target_vocabularies",
        width: 140,
        align: "center",
        render: (_, record) => {
          const vocabs = record.target_vocabularies || []
          if (!vocabs.length) return <Text type="secondary">—</Text>

          return (
            <Tooltip
              title={
                <div className="max-w-xs text-xs">
                  <div className="mb-1 font-semibold">Danh sách {vocabs.length} từ vựng:</div>
                  <div className="flex flex-wrap gap-1">
                    {vocabs.map((v, i) => (
                      <span key={i} className="rounded bg-gray-800 px-1 py-0.5 text-[11px] text-amber-300">
                        {v}
                      </span>
                    ))}
                  </div>
                </div>
              }
            >
              <Tag color="gold" className="m-0 cursor-pointer font-medium text-xs">
                {vocabs.length} từ vựng
              </Tag>
            </Tooltip>
          )
        },
      },
      {
        title: "Số câu hỏi",
        key: "question_counts",
        width: 160,
        align: "center",
        render: (_, record) => {
          const counts = record.question_counts || { cloze: 0, mcq: 0, tf: 0, total: 0 }
          if (!counts.total) return <Text type="secondary">0 câu</Text>

          return (
            <Tooltip
              title={
                <div className="text-xs">
                  <div>• Cloze (Điền từ): {counts.cloze} câu</div>
                  <div>• MCQ (Trắc nghiệm): {counts.mcq} câu</div>
                  <div>• TF (Đúng/Sai): {counts.tf} câu</div>
                </div>
              }
            >
              <div className="flex cursor-pointer items-center justify-center gap-1 font-mono text-xs">
                <span className="font-bold text-blue-600">{counts.total} câu</span>
                <span className="text-[11px] text-gray-400">
                  ({counts.cloze}C - {counts.mcq}M - {counts.tf}T)
                </span>
              </div>
            </Tooltip>
          )
        },
      },
      {
        title: "Trạng thái",
        dataIndex: "status",
        key: "status",
        width: 100,
        align: "center",
        render: (status: string) => {
          const info = STATUS_MAP[status]
          if (info) {
            return (
              <Tag color={info.color} className="m-0 font-medium text-xs">
                {info.label}
              </Tag>
            )
          }
          return <Tag className="m-0 text-xs">{status || "—"}</Tag>
        },
      },
      {
        title: "Thao tác",
        key: "actions",
        width: 110,
        align: "center",
        render: (_, record, index) => (
          <Button
            size="small"
            type="primary"
            ghost
            icon={<EyeOutlined />}
            onClick={() => onViewDetail(record, index, readings)}
            className="px-2 text-xs"
          >
            Chi tiết
          </Button>
        ),
      },
    ]

    return (
      <div className="rounded-md border border-blue-100 bg-blue-50/30 p-2.5">
        <div className="mb-2 flex items-center justify-between">
          <span className="font-semibold text-blue-800 text-xs">
            📑 Danh sách các bài đọc thuộc bộ "{setRecord.title}" ({readings.length} bài)
          </span>
        </div>
        <Table<ReadingContentItem>
          rowKey="id"
          columns={subColumns}
          dataSource={readings}
          pagination={false}
          size="small"
          bordered
          className="bg-white"
        />
      </div>
    )
  }

  // Main table columns (Sets)
  const columns: TableProps<ReadingSetItem>["columns"] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 75,
      align: "center",
      render: (val: number) => <span className="font-mono text-gray-500 text-xs">#{val}</span>,
    },
    {
      title: "Bộ từ (node_type = set)",
      key: "set_info",
      render: (_, record) => {
        const title = record.title
        const fullTitle = record.full_title
        const setUrl = record.id
          ? record.slug
            ? `https://jupi.vn/set/${record.slug}-${record.id}`
            : `https://jupi.vn/set/${record.id}`
          : null

        return (
          <div className="flex items-center gap-2.5">
            {record.image_url ? (
              <img
                src={record.image_url}
                alt={title}
                className="h-9 w-9 shrink-0 rounded border border-gray-200 object-cover"
              />
            ) : (
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-blue-50 text-blue-600">
                <FolderOutlined className="text-base" />
              </div>
            )}
            <div className="flex flex-col gap-0.5 leading-tight">
              <div className="flex items-center gap-1.5">
                {setUrl ? (
                  <a
                    href={setUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-gray-900 text-xs hover:text-primary hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {title}
                  </a>
                ) : (
                  <span className="font-semibold text-gray-900 text-xs">{title}</span>
                )}
                {setUrl && <LinkOutlined className="text-[10px] text-gray-400" />}
              </div>
              {fullTitle && fullTitle !== title && (
                <span className="max-w-[320px] truncate text-[11px] text-gray-500" title={fullTitle}>
                  {fullTitle}
                </span>
              )}
            </div>
          </div>
        )
      },
    },
    {
      title: "Loại bộ",
      dataIndex: "type",
      key: "type",
      width: 110,
      align: "center",
      render: (type: string, record) => {
        if (type === "system") {
          return (
            <Tag color="blue" className="m-0 font-medium text-xs">
              Hệ thống
            </Tag>
          )
        }
        return (
          <Tooltip title={record.user?.name ? `Tạo bởi: ${record.user.name} (${record.user.email})` : undefined}>
            <Tag color="orange" className="m-0 cursor-pointer font-medium text-xs">
              Người dùng
            </Tag>
          </Tooltip>
        )
      },
    },
    {
      title: "Số bài reading",
      dataIndex: "reading_count",
      key: "reading_count",
      width: 180,
      align: "center",
      render: (count: number, record) => {
        const parts = record.reading_contents?.map((c) => `P${c.part_number}`) || []
        return (
          <div className="flex flex-col items-center gap-1">
            <span className="font-bold font-mono text-blue-700 text-xs">{count} bài đọc</span>
            <div className="flex flex-wrap justify-center gap-1">
              {parts.map((p, idx) => (
                <Tag key={idx} color="cyan" className="m-0 px-1 py-0 text-[10px] leading-tight">
                  {p}
                </Tag>
              ))}
            </div>
          </div>
        )
      },
    },
    {
      title: "Ngày tạo",
      dataIndex: "created_at",
      key: "created_at",
      width: 140,
      align: "center",
      render: (val: string) => {
        if (!val) return "—"
        const d = dayjs(val)
        return (
          <div className="flex flex-col font-mono text-xs leading-tight">
            <span className="font-medium text-gray-800">{d.format("DD/MM/YYYY")}</span>
            <Text type="secondary" className="text-[11px]">
              {d.format("HH:mm")}
            </Text>
          </div>
        )
      },
    },
    {
      title: "Thao tác",
      key: "actions",
      width: 140,
      align: "center",
      render: (_, record) => {
        const isExpanded = expandedRowKeys.includes(record.id)
        return (
          <Button
            size="small"
            type="link"
            icon={isExpanded ? <DownOutlined /> : <RightOutlined />}
            onClick={() => {
              if (isExpanded) {
                setExpandedRowKeys(expandedRowKeys.filter((k) => k !== record.id))
              } else {
                setExpandedRowKeys([...expandedRowKeys, record.id])
              }
            }}
            className="font-medium text-xs"
          >
            {isExpanded ? "Thu gọn" : "Xem bài đọc"}
          </Button>
        )
      },
    },
  ]

  const isAllExpanded = data.length > 0 && expandedRowKeys.length === data.length

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col" ref={tableWrapperRef}>
      <div className="mb-2 flex items-center justify-between px-1">
        <span className="text-gray-500 text-xs">
          Hiển thị <strong>{data.length}</strong> bộ từ có bài reading
        </span>
        {data.length > 0 && (
          <Button size="small" onClick={handleToggleExpandAll} className="text-xs">
            {isAllExpanded ? "Thu gọn tất cả bộ" : "Mở rộng tất cả bộ"}
          </Button>
        )}
      </div>

      <Table<ReadingSetItem>
        bordered
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={pagination as TablePaginationConfig}
        expandable={{
          expandedRowRender,
          expandedRowKeys,
          onExpandedRowsChange: (newKeys) => setExpandedRowKeys(newKeys as React.Key[]),
        }}
        scroll={{ x: "100%", y: scrollConfig.y }}
        size="small"
        classNames={{
          root: "full-height-table",
        }}
      />
    </div>
  )
}

export default ReadingContentTable
